import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";
import { authService } from "@/services/authService";
import logo from "@/assets/images/logo.png";

export function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  
  // Get token from query params
  const token = new URLSearchParams(location.search).get("token");

  // Get email from query params (if passed)
  const email = new URLSearchParams(location.search).get("email");

  // Validate token on component mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      toast.error("Invalid reset link", {
        description: "The password reset link is invalid or has expired.",
        duration: 4000,
      });
      
      // Redirect to forgot password after showing error
      setTimeout(() => {
        navigate("/forgot-password");
      }, 3000);
    }
  }, [token, navigate]);

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required("Please confirm your password"),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      password_confirmation: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!tokenValid) return;
      
      setIsSubmitting(true);
      try {
        // Call API to reset password
        await authService.resetPassword({
          token,
          email,
          password: values.password,
          password_confirmation: values.password_confirmation
        });
        
        toast.success("Password Updated", {
          description: "Your password has been reset successfully!",
          duration: 4000,
          position: "top-right",
        });
        
        // Redirect to login page after 2 seconds
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } catch (error) {
        console.error("Password Reset Error:", error);
        
        if (error.response?.status === 422) {
          // Validation errors
          const errorMessage = error.response?.data?.message || "Please check your input";
          toast.error("Invalid Input", {
            description: errorMessage,
            duration: 4000,
            position: "top-right",
          });
        } else if (error.response?.status === 400 || error.response?.status === 404) {
          // Invalid token
          setTokenValid(false);
          toast.error("Invalid Reset Link", {
            description: "The password reset link is invalid or has expired.",
            duration: 4000,
            position: "top-right",
          });
          
          // Redirect to forgot password
          setTimeout(() => {
            navigate("/forgot-password");
          }, 3000);
        } else {
          // Generic error
          toast.error("Reset Failed", {
            description: error.response?.data?.message || "Something went wrong. Please try again.",
            duration: 4000,
            position: "top-right",
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F1EBDB' }}>
        <Toaster richColors position="top-right" />
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Invalid Reset Link</h2>
          <p className="text-gray-700 mb-6">Redirecting to forgot password page...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster
        richColors
        position="top-right"
        toastOptions={{
          style: {
            zIndex: 9999,
          },
          duration: 3000,
        }}
      />
      <div className="min-h-screen flex items-center justify-center flex-col" style={{ backgroundColor: '#F1EBDB' }}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg"
        >
          <div>
            <h2 className="text-3xl font-bold text-center text-black">Reset Password</h2>
            <p className="mt-2 text-sm text-[#F16745] text-center mb-8">
              Please enter your new password
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} className="space-y-6">
            <div className="rounded-md space-y-4">
              <div>
                <label htmlFor="password" className="block  font-medium text-gray-700">
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  className="mt-1 appearance-none text-black relative block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] focus:z-10 sm:text-sm"
                  placeholder="Enter new password"
                />
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="password_confirmation" className="block font-medium text-gray-700">
                  Confirm Password
                </label>
                <input
                  id="password_confirmation"
                  type="password"
                  name="password_confirmation"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password_confirmation}
                  className="mt-1 appearance-none text-black relative block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] focus:z-10 sm:text-sm"
                  placeholder="Confirm new password"
                />
                {formik.touched.password_confirmation && formik.errors.password_confirmation && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.password_confirmation}</p>
                )}
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={isSubmitting || !formik.values.password || !formik.values.password_confirmation}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#F16745] hover:bg-[#F16745]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-150 hover:scale-[1.02]"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </span>
              ) : (
                "Reset Password"
              )}
            </motion.button>
          </form>
        </motion.div>
        <div className="mt-8 mb-4">
          <img src={logo} alt="PostScrypt Logo" className="h-16 w-auto" />
        </div>
      </div>
    </>
  );
}